import {
    ChallengeLeaderboardType,
    LeaderboardGenderMode,
    LeaderboardActivityCategoryFilter,
    LeaderboardMetric,
    ActivityType,
    WorkoutCategory
} from '@core/api/ChallengeClient';
import { format, parse } from 'date-fns';

/**
 * Auto-configure activity category filter fields based on selected metric.
 * Each metric type has specific field requirements.
 */
export const getAutoConfiguredFields = (
    metric: LeaderboardMetric,
    currentFilter: LeaderboardActivityCategoryFilter
): LeaderboardActivityCategoryFilter => {
    const baseFilter = { ...currentFilter, metric };
    const withoutChallengeFields = {
        ...baseFilter,
        challengeId: undefined,
        challengeLeaderboardType: undefined
    };

    switch (metric) {
        case LeaderboardMetric.CHALLENGE:
            return {
                ...baseFilter,
                activityType: undefined,
                workoutCategory: undefined,
                workoutType: undefined,
                rewardCurrencyCode: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.STEPS:
            return {
                ...withoutChallengeFields,
                activityType: ActivityType.DAILY_STEPS,
                workoutCategory: undefined,
                workoutType: undefined,
                rewardCurrencyCode: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.DISTANCE:
            return {
                ...withoutChallengeFields,
                activityType: ActivityType.WORKOUT,
                workoutType: undefined,
                rewardCurrencyCode: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.TIME:
            return {
                ...withoutChallengeFields,
                activityType: ActivityType.FACILITY_VISIT,
                workoutCategory: WorkoutCategory.STATIONARY_TRAININGS,
                workoutType: undefined,
                rewardCurrencyCode: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.REWARDS:
            return {
                ...withoutChallengeFields,
                activityType: undefined,
                workoutCategory: undefined,
                workoutType: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.ACTIVITY_POINTS:
            return {
                ...withoutChallengeFields,
                activityType: undefined,
                workoutCategory: undefined,
                workoutType: undefined,
                rewardCurrencyCode: undefined,
                pointTypeKey: undefined
            };

        case LeaderboardMetric.COMPOSITE_POINTS:
            return {
                ...withoutChallengeFields,
                activityType: undefined,
                workoutCategory: undefined,
                workoutType: undefined,
                rewardCurrencyCode: undefined
            };

        default:
            return withoutChallengeFields;
    }
};

/**
 * The support API rejects these combinations with an indistinguishable 400,
 * so they are caught here to say which filter is wrong and why.
 */
export const getChallengeFilterError = (
    filters: LeaderboardActivityCategoryFilter[]
): string | null => {
    for (const [index, filter] of filters.entries()) {
        if (filter.metric !== LeaderboardMetric.CHALLENGE) continue;

        const position = `Activity filter ${index + 1}`;
        if (!filter.challengeId) {
            return `${position}: pick a challenge.`;
        }
        if (!filter.challengeLeaderboardType) {
            return `${position}: pick which of the challenge's leaderboards to show.`;
        }
        if (!filter.label || !Object.values(filter.label).some((text) => text?.trim())) {
            return `${position}: a challenge tab needs a label.`;
        }
        if (
            filter.genderFilter !== LeaderboardGenderMode.DISABLED &&
            filter.challengeLeaderboardType !== ChallengeLeaderboardType.MEMBER
        ) {
            return `${position}: a gender split only applies to a members leaderboard.`;
        }
    }
    return null;
};

/**
 * Helper to format dates for API (YYYY-MM-DD) in local timezone
 */
export const formatDateForApi = (date: Date | null): string | undefined => {
    return date ? format(date, 'yyyy-MM-dd') : undefined;
};

/**
 * Helper to parse date strings to Date objects in local timezone
 */
export const parseDateString = (dateString?: string): Date | null => {
    if (!dateString) return null;
    try {
        return parse(dateString, 'yyyy-MM-dd', new Date());
    } catch (e) {
        console.error('Error parsing date string:', e);
        return null;
    }
};
